import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  MapPin,
  ExternalLink,
  Info,
  Layers,
  CircleX,
  Building2,
} from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import "../styles/CharteredSchools.css";
import "../styles/UtahCities.css";
import utahCounties from "../assets/utah-counties.json";
import citiesData from "./cities.json";

const BRAND = {
  darkBlue: "#203feb",
  red: "#D62828",
  dark: "#1e293b",
  blue: "#3b82f6",
};

const fipsToCounty = {
  "49001": "Beaver",
  "49003": "Box Elder",
  "49005": "Cache",
  "49007": "Carbon",
  "49009": "Daggett",
  "49011": "Davis",
  "49013": "Duchesne",
  "49015": "Emery",
  "49017": "Garfield",
  "49019": "Grand",
  "49021": "Iron",
  "49023": "Juab",
  "49025": "Kane",
  "49027": "Millard",
  "49029": "Morgan",
  "49031": "Piute",
  "49033": "Rich",
  "49035": "Salt Lake",
  "49037": "San Juan",
  "49039": "Sanpete",
  "49041": "Sevier",
  "49043": "Summit",
  "49045": "Tooele",
  "49047": "Uintah",
  "49049": "Utah",
  "49051": "Wasatch",
  "49053": "Washington",
  "49055": "Wayne",
  "49057": "Weber",
};

const parseCountyList = (countyValue = "") =>
  countyValue
    .split("/")
    .map((county) => county.trim())
    .filter(Boolean);

const UtahCities = () => {
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tooltip, setTooltip] = useState("");
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const cities = useMemo(
    () =>
      citiesData.map((city) => ({
        ...city,
        counties: parseCountyList(city.county),
      })),
    []
  );

  const countyCounts = useMemo(() => {
    const counts = {};

    cities.forEach((city) => {
      city.counties.forEach((county) => {
        counts[county] = (counts[county] || 0) + 1;
      });
    });

    return counts;
  }, [cities]);

  const maxCities = useMemo(() => {
    const counts = Object.values(countyCounts);
    return counts.length ? Math.max(...counts) : 0;
  }, [countyCounts]);

  const colorScale = scaleLinear()
    .domain([0, maxCities || 1])
    .range(["#f1f5f9", BRAND.blue]);

  const filteredCities = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return cities.filter((city) => {
      const matchesCounty = selectedCounty
        ? city.counties.includes(selectedCounty)
        : true;

      const matchesQuery = query
        ? [
            city.name,
            city.county,
            city.type,
            city.area,
            city.elevation,
            city.year,
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(query))
        : true;

      return matchesCounty && matchesQuery;
    });
  }, [cities, searchQuery, selectedCounty]);

  const cityTypeCount = useMemo(
    () => new Set(cities.map((city) => city.type).filter(Boolean)).size,
    [cities]
  );

  const activeCountyLabel = selectedCounty ? `${selectedCounty} County` : "Search Results";

  const generateMapLink = (cityName) =>
    `https://www.google.com/maps?q=${encodeURIComponent(`${cityName}, Utah`)}`;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white">
      <LandingSection
        data={{ imageName: "cities.webp", title: "Cities", visible: true }}
      />

      <div className="charter-explorer-container">
        <div className="cities-intro card-glass">
          <div>
            <p className="cities-eyebrow">Utah communities atlas</p>
            <h2>Explore Utah cities through an interactive county map</h2>
          </div>
          <p>
            Search by city name, county, type, elevation, area, or founding year,
            then use the map to focus on the communities inside each county.
          </p>
        </div>

        <div className="explorer-grid">
          <div className="map-panel">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="card-glass map-container"
            >
              <div className="map-header">
                <h3>
                  <Layers size={20} /> Interactive County Map
                </h3>
                <p>Click a county to see the cities and towns mapped to it</p>
              </div>

              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 6000, center: [-111.9, 39.3] }}
                className="rsm-map"
              >
                <Geographies geography={utahCounties}>
                  {({ geographies }) =>
                    geographies
                      .filter((geo) => geo.id.startsWith("49"))
                      .map((geo) => {
                        const countyName = fipsToCounty[geo.id];
                        const cityCount = countyCounts[countyName] || 0;

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={
                              selectedCounty === countyName
                                ? BRAND.red
                                : colorScale(cityCount)
                            }
                            stroke="#fff"
                            strokeWidth={1}
                            onMouseEnter={(event) => {
                              setTooltip(`${countyName}: ${cityCount} cities`);
                              setTooltipPos({
                                x: event.clientX,
                                y: event.clientY,
                              });
                            }}
                            onMouseMove={(event) => {
                              setTooltipPos({
                                x: event.clientX,
                                y: event.clientY,
                              });
                            }}
                            onMouseLeave={() => setTooltip("")}
                            onClick={() => setSelectedCounty(countyName)}
                            style={{
                              default: {
                                outline: "none",
                                transition: "all 0.3s",
                              },
                              hover: {
                                fill: BRAND.red,
                                outline: "none",
                                cursor: "pointer",
                              },
                              pressed: { outline: "none" },
                            }}
                          />
                        );
                      })
                  }
                </Geographies>
              </ComposableMap>

              <div className="map-legend">
                <span>0 Cities</span>
                <div className="legend-gradient" />
                <span>{maxCities} Cities</span>
              </div>
            </motion.div>
          </div>

          <div className="data-panel">
            <div className="stats-row">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                className="stat-card"
              >
                <Building2 size={24} color={BRAND.darkBlue} />
                <div>
                  <span className="stat-val">{cities.length}</span>
                  <span className="stat-lab">Total Utah Cities</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="stat-card"
              >
                <MapPin size={24} color={BRAND.red} />
                <div>
                  <span className="stat-val">{Object.keys(countyCounts).length}</span>
                  <span className="stat-lab">Counties Represented</span>
                </div>
              </motion.div>
            </div>

            <div className="stats-row city-stats-row">
              <div className="stat-card">
                <Info size={24} color={BRAND.blue} />
                <div>
                  <span className="stat-val">{cityTypeCount}</span>
                  <span className="stat-lab">Community Types</span>
                </div>
              </div>

              <div className="stat-card">
                <Layers size={24} color={BRAND.red} />
                <div>
                  <span className="stat-val">{filteredCities.length}</span>
                  <span className="stat-lab">
                    {selectedCounty || searchQuery ? "Filtered Results" : "Visible Cities"}
                  </span>
                </div>
              </div>
            </div>

            <div className="search-box card-glass">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search by city name, county, type, year..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {(searchQuery || selectedCounty) && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCounty(null);
                  }}
                  title="Clear all filters"
                >
                  <CircleX size={18} />
                </button>
              )}
            </div>

            <div className="results-container">
              <AnimatePresence mode="wait">
                {selectedCounty || searchQuery ? (
                  <motion.div
                    key={selectedCounty || searchQuery || "city-results"}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="county-results"
                  >
                    <div className="county-header">
                      <h2>{activeCountyLabel}</h2>
                      <div className="header-actions">
                        <span className="count-badge">
                          {filteredCities.length} Result
                          {filteredCities.length !== 1 ? "s" : ""}
                        </span>
                        <button
                          className="clear-all-small"
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedCounty(null);
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    {filteredCities.length > 0 ? (
                      <div className="school-list">
                        {filteredCities.map((city, index) => (
                          <motion.div
                            key={`${city.name}-${city.county}-${index}`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="school-card card-glass"
                          >
                            <div className="city-card-header">
                              <div>
                                <h4>{city.name}</h4>
                                <p className="city-card-subtitle">
                                  {city.type} in {city.county} County
                                </p>
                              </div>
                              <a
                                href={generateMapLink(city.name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="city-map-link"
                              >
                                View Map <ExternalLink size={14} />
                              </a>
                            </div>

                            <div className="city-meta-grid">
                              <span>
                                <MapPin size={14} /> County: {city.county}
                              </span>
                              <span>Type: {city.type}</span>
                              <span>Area: {city.area || "Not listed"}</span>
                              <span>
                                Elevation: {city.elevation || "Not listed"}
                              </span>
                              <span>Founded: {city.year || "Not listed"}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-data">
                        <Info size={40} />
                        <p>No cities found for the current search or county filter.</p>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="welcome-box card-glass"
                  >
                    <Building2 size={60} />
                    <h2>Utah Cities Explorer</h2>
                    <p>
                      Select a county on the map or search for a city to explore
                      Utah communities in a map-first view.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <div
        className="charter-explorer-container"
        style={{ paddingBottom: "2rem" }}
      >
        <div className="card-glass" style={{ padding: "2rem", marginTop: "2rem" }}>
          <div className="flex items-center gap-2 mb-4">
            <Info size={24} color={BRAND.darkBlue} />
            <h3 className="text-xl font-bold" style={{ color: BRAND.dark }}>
              Data Sources
            </h3>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-sm opacity-70 mb-2">
              For more detailed information, please visit the following resources:
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://www.utah-demographics.com/cities_by_population"
                target="_blank"
                rel="noopener noreferrer"
                className="school-link flex items-center gap-1"
              >
                Utah Demographics - Cities by Population{" "}
                <ExternalLink size={14} />
              </a>
              <a
                href="https://www.utah-demographics.com/counties-cities-that-begin-with-A"
                target="_blank"
                rel="noopener noreferrer"
                className="school-link flex items-center gap-1"
              >
                Utah Demographics - Counties and Cities{" "}
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {tooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{
              position: "fixed",
              top: tooltipPos.y - 50,
              left: tooltipPos.x + 20,
              pointerEvents: "none",
              zIndex: 9999,
              background: "rgba(15, 23, 42, 0.9)",
              backdropFilter: "blur(8px)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: "600",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              whiteSpace: "nowrap",
            }}
          >
            {tooltip}
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default UtahCities;
